import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/useAuth';
import { useBudgetList, useApproveBudgetRequest, useDenyBudgetRequest, useEditBudgetRequest, useDeleteBudgetRequest } from '../../hooks/useBudgetMutations';
import { useBrgList } from '../../hooks/useBrgList';
import { useUserAssignedBrgs } from '../../hooks/useUserAssignedBrgs';
import { useFiscalYears } from '../../hooks/useFiscalYears';
import { BudgetRequestListItemDto } from '../../types';
import { BudgetToolbar } from './budget/BudgetToolbar';
import { SummaryCards } from './budget/SummaryCards';
import { BudgetVsSpendingChart } from './budget/BudgetVsSpendingChart';
import { RecentBudgetActivityTable } from './budget/RecentBudgetActivityTable';
import { BudgetRequestDetailsModal } from './budget/BudgetRequestDetailsModal';
import { BudgetRequestReviewModal } from './budget/BudgetRequestReviewModal';
import { BudgetRequestEditModal } from './budget/BudgetRequestEditModal';
import { BudgetDeleteConfirmationModal } from './budget/BudgetDeleteConfirmationModal';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { Alert } from '../../components/shared/Alert';
import { ExportButton } from '../../components/shared/ExportButton';
import { useBudgetStatuses } from '../../hooks/useBudgetStatuses';
import { usersApi } from '../../api/users';
import { UserDropdownDto } from '../../types';
import { buildExportFileName, buildScopeLabel, exportToExcel } from '../../lib/export';
import {
  buildBudgetExportFilters,
  buildBudgetExportRows,
  buildBudgetExportSheet,
} from './budget/budgetExport';

export function BudgetDashboard() {
  const { roles, name: userName } = useAuth();
  const location = useLocation();
  const { data: budgets = [], isPending, error } = useBudgetList();
  const { data: brgs = [] } = useBrgList();
  const { data: userAssignedBrgs = [] } = useUserAssignedBrgs();
  const { data: masterFiscalYears = [] } = useFiscalYears();
  const approveMutation = useApproveBudgetRequest();
  const denyMutation = useDenyBudgetRequest();
  const editMutation = useEditBudgetRequest();
  const deleteMutation = useDeleteBudgetRequest();

  // Get fiscal years from master table (MDM_FiscalYears), sorted in descending order
  const fiscalYears = useMemo(() => {
    // Filter active fiscal years and extract their codes
    const activeFiscalYears = masterFiscalYears
      .filter((fy) => fy.isActive)
      .map((fy) => fy.fiscalYearCode)
      .sort((a, b) => b.localeCompare(a)); // Sort descending
    
    return activeFiscalYears;
  }, [masterFiscalYears]);

  // Default to current fiscal year (FY + current year), fallback to most recent.
  const defaultFiscalYear = useMemo(() => {
    const currentYearCode = `FY ${new Date().getFullYear()}`;
    if (fiscalYears.includes(currentYearCode)) return currentYearCode;
    return fiscalYears.length > 0 ? fiscalYears[0] : currentYearCode;
  }, [fiscalYears]);

  const [selectedFiscalYear, setSelectedFiscalYear] = useState<string>('');

  // Initialize selected fiscal year once years are available.
  useEffect(() => {
    if (!selectedFiscalYear && defaultFiscalYear) {
      setSelectedFiscalYear(defaultFiscalYear);
    }
  }, [defaultFiscalYear, selectedFiscalYear]);

  const [selectedBrg, setSelectedBrg] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<BudgetRequestListItemDto | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'review' | 'edit' | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAllBudgets, setShowAllBudgets] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [highlightedBudgetId, setHighlightedBudgetId] = useState<number | null>(null);

  const isAdmin = roles?.includes('BRG.Admin');
  
  // Track which budget request ID we've already opened from URL to prevent re-opening on close
  const openedBudgetRequestRef = useRef<number | null>(null);

  // Handle opening budget request from email deep-link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const budgetRequestId = params.get('openBudgetRequest');
    
    // Only process if we have both the URL param and loaded budgets
    if (!budgetRequestId || budgets.length === 0) {
      return;
    }
    
    const requestIdNum = parseInt(budgetRequestId, 10);
    
    // If we've already opened this budget request from the URL, don't open it again
    if (openedBudgetRequestRef.current === requestIdNum) {
      return;
    }
    
    const matchingBudget = budgets.find((b: any) => b.budgetRequestId === requestIdNum);
    
    if (matchingBudget) {
      // Mark this request ID as opened
      openedBudgetRequestRef.current = requestIdNum;
      
      // Auto-open the review modal for the budget request
      setSelectedRequest(matchingBudget);
      setModalMode(isAdmin ? 'review' : 'view');
      
      // Clean up URL to remove query parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Scroll to the table
      setTimeout(() => {
        const table = document.getElementById('recent-budget-activity-table');
        if (table) {
          table.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [location.search, budgets, isAdmin]);

  // Handle highlighting of recognition-linked budget
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const recognitionId = params.get('highlightRecognition');
    
    if (recognitionId && budgets.length > 0) {
      // Find budget entry with matching MmRecognitionId
      const matchingBudget = budgets.find((b: any) => b.mmRecognitionId === parseInt(recognitionId));
      if (matchingBudget) {
        setHighlightedBudgetId(matchingBudget.budgetRequestId);
        // Scroll to the table and remove the query parameter
        setTimeout(() => {
          const table = document.getElementById('recent-budget-activity-table');
          if (table) {
            table.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          // Remove the query parameter
          window.history.replaceState({}, document.title, window.location.pathname);
        }, 100);
        
        // Clear the highlight after animation completes (5 seconds)
        setTimeout(() => {
          setHighlightedBudgetId(null);
        }, 5000);
      }
    }
  }, [location.search, budgets]);

  // Create a map of brgId to brgName
  const brgNameMap = useMemo(() => {
    const nameMap = new Map<number, string>();
    brgs.forEach((b) => {
      nameMap.set(b.brgId, b.name);
    });
    return nameMap;
  }, [brgs]);

  // Get BRGs to display in filter.
  // Admins see all BRGs; non-admins see only assigned BRGs.
  const brgFilterOptions = useMemo(() => {
    if (isAdmin) {
      return brgs
        .map((b) => [b.brgId, b.name] as [number, string])
        .sort((a, b) => a[1].localeCompare(b[1]));
    }

    return userAssignedBrgs
      .map((b) => [b.brgId, b.name] as [number, string])
      .sort((a, b) => a[1].localeCompare(b[1]));
  }, [isAdmin, brgs, userAssignedBrgs]);

  // Filter budgets based on role and selected filters
  const filteredBudgets = useMemo(() => {
    let filtered = [...budgets];

    // Hide denied budget requests that were created from projects (budgetStatusId = 3 AND projectId is set)
    filtered = filtered.filter((b) => !(b.budgetStatusId === 3 && b.projectId));

    // Role-based filtering: leads can only see their assigned BRGs
    if (!isAdmin) {
      const assignedBrgIds = userAssignedBrgs.map(brg => brg.brgId);
      filtered = filtered.filter((b) => assignedBrgIds.includes(b.brgId));
    }

    // Apply fiscal year filter
    // Only filter if a non-default fiscal year is selected
    if (selectedFiscalYear && selectedFiscalYear !== defaultFiscalYear) {
      const yearValue = selectedFiscalYear.replace('FY ', '');
      filtered = filtered.filter((b) =>
        b.fiscalYear === selectedFiscalYear || b.fiscalYear === yearValue
      );
    }

    // Apply BRG filter
    if (selectedBrg) {
      filtered = filtered.filter((b) => b.brgId === selectedBrg);
    }

    return filtered;
  }, [budgets, selectedBrg, selectedFiscalYear, isAdmin, userAssignedBrgs, defaultFiscalYear]);

  // Calculate summary data
  const summaryData = useMemo(() => {
    const totalAllocated = filteredBudgets.reduce((sum, b) => sum + (b.requestAmount || 0), 0);
    const totalSpent = filteredBudgets.reduce((sum, b) => sum + (b.spentAmount || 0), 0);

    return {
      totalAllocated,
      totalSpent,
      remaining: totalAllocated - totalSpent,
      utilizationPercent: totalAllocated > 0 ? Math.round((totalSpent / totalAllocated) * 100) : 0,
    };
  }, [filteredBudgets]);

  // Let RecentBudgetActivityTable handle search filtering to avoid double filtering.
  const displayedBudgets = useMemo(() => {
    // Limit to top 10 if showAllBudgets is false
    if (!showAllBudgets) {
      return filteredBudgets.slice(0, 10);
    }

    return filteredBudgets;
  }, [filteredBudgets, showAllBudgets]);

  // ─── Export ──────────────────────────────────────────────────────────────
  // Same lookups RecentBudgetActivityTable uses, so exported labels match the
  // grid rather than showing raw ids. Both share the React Query cache.
  const { data: budgetStatuses = [] } = useBudgetStatuses();
  const { data: allUsers = [] } = useQuery<UserDropdownDto[], Error>({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(undefined, false),
  });

  const exportLookups = useMemo(
    () => ({
      brgNameById: brgNameMap,
      statusNameById: new Map(
        budgetStatuses.map((s) => [s.budgetStatusId, s.budgetStatusName] as const)
      ),
      userNameById: new Map(allUsers.map((u) => [u.userId, u.displayName] as const)),
    }),
    [brgNameMap, budgetStatuses, allUsers]
  );

  // Mirrors the search predicate inside RecentBudgetActivityTable. The export
  // covers every matching request, not just the 10 rows the table previews.
  const exportBudgets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return filteredBudgets;
    return filteredBudgets.filter((b) => {
      const userName = b.requestedById
        ? allUsers.find((u) => u.userId === b.requestedById)?.displayName || ''
        : '';
      return `${b.purposeDescription || ''} ${userName}`.toLowerCase().includes(query);
    });
  }, [filteredBudgets, searchQuery, allUsers]);

  const handleExportExcel = useCallback(async () => {
    const rows = buildBudgetExportRows(exportBudgets, exportLookups);
    const totalAllocated = exportBudgets.reduce((sum, b) => sum + (b.requestAmount || 0), 0);
    const totalSpent = exportBudgets.reduce((sum, b) => sum + (b.spentAmount || 0), 0);

    await exportToExcel(
      {
        title: 'BRG Budget Dashboard',
        fileName: buildExportFileName('BRG-Budget-Dashboard'),
        filters: buildBudgetExportFilters({
          fiscalYear: selectedFiscalYear || defaultFiscalYear,
          selectedBrgId: selectedBrg,
          searchQuery,
          brgNameById: brgNameMap,
        }),
        scopeLabel: buildScopeLabel(
          roles,
          brgFilterOptions.map(([, name]) => name)
        ),
        exportedBy: userName,
      },
      [
        buildBudgetExportSheet(rows, {
          totalAllocated,
          totalSpent,
          remaining: totalAllocated - totalSpent,
          utilizationPercent:
            totalAllocated > 0 ? Math.round((totalSpent / totalAllocated) * 100) : 0,
        }),
      ]
    );
  }, [
    exportBudgets,
    exportLookups,
    selectedFiscalYear,
    defaultFiscalYear,
    selectedBrg,
    searchQuery,
    brgNameMap,
    brgFilterOptions,
    roles,
    userName,
  ]);

  // Prepare data for chart
  const chartData = useMemo(() => {
    const grouped = new Map<number, { allocated: number; spent: number }>();
    filteredBudgets.forEach((b) => {
      const existing = grouped.get(b.brgId) || { allocated: 0, spent: 0 };
      existing.allocated += b.requestAmount || 0;
      existing.spent += b.spentAmount || 0;
      grouped.set(b.brgId, existing);
    });
    return Array.from(grouped.entries()).map(([brgId, data]) => ({
      brgId,
      brgName: brgNameMap.get(brgId) || `BRG ${brgId}`,
      allocated: data.allocated,
      spent: data.spent,
    }));
  }, [filteredBudgets, brgNameMap]);

  const handleViewRequest = (request: BudgetRequestListItemDto) => {
    setSelectedRequest(request);
    // Admin users see the review modal (with Approve/Deny buttons)
    // Non-admin users see the details modal (with Edit/Delete buttons for Pending requests)
    setModalMode(isAdmin ? 'review' : 'view');
  };

  const handleViewAll = () => {
    setShowAllBudgets(true);
  };

  const handleEditRequest = (request: BudgetRequestListItemDto) => {
    setSelectedRequest(request);
    setModalMode('edit');
  };

  const handleDeleteRequest = (request: BudgetRequestListItemDto) => {
    setSelectedRequest(request);
    setShowDeleteConfirm(true);
  };

  const handleApprove = () => {
    if (selectedRequest) {
      approveMutation.mutate(selectedRequest.budgetRequestId, {
        onSuccess: () => {
          setNotification({
            message: 'Budget request has been approved. The lead will be notified by email.',
            type: 'success',
          });
          setModalMode(null);
          setSelectedRequest(null);
          setTimeout(() => setNotification(null), 5000);
        },
        onError: () => {
          setNotification({
            message: 'Failed to approve budget request. Please try again.',
            type: 'error',
          });
          setTimeout(() => setNotification(null), 5000);
        },
      });
    }
  };

  const handleDeny = (denyReason: string) => {
    if (selectedRequest) {
      denyMutation.mutate({ id: selectedRequest.budgetRequestId, denyReason }, {
        onSuccess: () => {
          setNotification({
            message: 'Budget request has been denied. The lead will be notified by email.',
            type: 'success',
          });
          setModalMode(null);
          setSelectedRequest(null);
          setTimeout(() => setNotification(null), 5000);
        },
        onError: () => {
          setNotification({
            message: 'Failed to deny budget request. Please try again.',
            type: 'error',
          });
          setTimeout(() => setNotification(null), 5000);
        },
      });
    }
  };

  const handleSaveEdit = (updatedData: any) => {
    if (selectedRequest) {
      editMutation.mutate(
        {
          id: selectedRequest.budgetRequestId,
          data: {
            brgId: updatedData.brgId,
            projectEvent: updatedData.projectEvent,
            requestAmount: updatedData.requestAmount,
            scheduledDate: updatedData.scheduledDate ? new Date(updatedData.scheduledDate).toISOString().split('T')[0] : undefined,
            purposeDescription: updatedData.purposeDescription,
            requestDescription: updatedData.requestDescription,
          },
        },
        {
          onSuccess: () => {
            setNotification({
              message: 'Budget request was successfully updated.',
              type: 'success',
            });
            setModalMode(null);
            setSelectedRequest(null);
            setTimeout(() => setNotification(null), 5000);
          },
          onError: () => {
            setNotification({
              message: 'Failed to update budget request. Please try again.',
              type: 'error',
            });
            setTimeout(() => setNotification(null), 5000);
          },
        }
      );
    }
  };

  const handleConfirmDelete = () => {
    if (selectedRequest) {
      deleteMutation.mutate(selectedRequest.budgetRequestId, {
        onSuccess: () => {
          setNotification({
            message: 'Budget request was successfully deleted.',
            type: 'success',
          });
          setShowDeleteConfirm(false);
          setSelectedRequest(null);
          setTimeout(() => setNotification(null), 5000);
        },
        onError: () => {
          setNotification({
            message: 'Failed to delete budget request. Please try again.',
            type: 'error',
          });
          setTimeout(() => setNotification(null), 5000);
        },
      });
    }
  };

  if (isPending) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert type="error" message="Something went wrong. Please try again." autoDismiss={5000} />
    );
  }

  const isEmpty = filteredBudgets.length === 0;

  return (
    <div className={`min-h-screen bg-gray-50 ${notification ? 'pt-16' : ''}`}>
      {/* Notification Banner */}
      {notification && (
        <Alert
          type={notification.type}
          message={notification.message}
          autoDismiss={5000}
          onDismiss={() => setNotification(null)}
        />
      )}

      {/* Empty State Banner */}
      {isEmpty && (
        <Alert
          type="info"
          message="No budget data available for the selected filters."
          autoDismiss={5000}
        />
      )}

      <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
        {/* Toolbar with Filters */}
        <BudgetToolbar
          fiscalYears={fiscalYears}
          selectedFiscalYear={selectedFiscalYear}
          onFiscalYearChange={setSelectedFiscalYear}
          brgs={brgFilterOptions}
          selectedBrg={selectedBrg}
          onBrgChange={setSelectedBrg}
          exportAction={
            <ExportButton
              format="excel"
              onExport={handleExportExcel}
              disabled={exportBudgets.length === 0}
              disabledReason="There are no budget requests to export for the current filters."
              testId="export-budget-excel"
            />
          }
        />

        {/* Summary Cards */}
        <SummaryCards
          totalAllocated={summaryData.totalAllocated}
          totalSpent={summaryData.totalSpent}
          remainingBudget={summaryData.remaining}
          utilizationPercent={summaryData.utilizationPercent}
          fiscalYear={selectedFiscalYear || defaultFiscalYear}
        />

        {/* Budget vs Spending Chart */}
        <BudgetVsSpendingChart data={chartData} />

        {/* Recent Budget Activity */}
        <RecentBudgetActivityTable
          budgets={displayedBudgets}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onViewRequest={handleViewRequest}
          onEditRequest={handleEditRequest}
          onDeleteRequest={handleDeleteRequest}
          onViewAll={handleViewAll}
          isAdmin={isAdmin}
          highlightedBudgetId={highlightedBudgetId}
          onHighlightClear={() => setHighlightedBudgetId(null)}
        />
      </div>

      {/* Modals - Rendered outside constrained container for proper fixed positioning */}
      {selectedRequest && modalMode === 'view' && (
        <BudgetRequestDetailsModal
          request={selectedRequest}
          onClose={() => {
            setModalMode(null);
            setSelectedRequest(null);
          }}
          onEdit={handleEditRequest}
          onDelete={handleDeleteRequest}
          isAdmin={isAdmin}
        />
      )}

      {selectedRequest && modalMode === 'review' && (
        <BudgetRequestReviewModal
          request={selectedRequest}
          onClose={() => {
            setModalMode(null);
            setSelectedRequest(null);
          }}
          onApprove={handleApprove}
          onDeny={handleDeny}
          isLoading={approveMutation.isPending || denyMutation.isPending}
        />
      )}

      {selectedRequest && modalMode === 'edit' && (
        <BudgetRequestEditModal
          request={selectedRequest}
          onClose={() => {
            setModalMode(null);
            setSelectedRequest(null);
          }}
          onSave={handleSaveEdit}
          isLoading={editMutation.isPending}
        />
      )}

      {showDeleteConfirm && selectedRequest && (
        <BudgetDeleteConfirmationModal
          request={selectedRequest}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setSelectedRequest(null);
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
